import { createPortal } from "react-dom";

/**
 * Shown when the coach tries to sign out while OFFLINE with unsaved plays.
 * Offline we can't upload, so signing out would wipe that data — warn and let
 * them cancel (reconnect first) or log out anyway.
 *
 * Portaled to document.body so the sidebar drawer's stacking context
 * (fixed z-[99990]) can't trap it; same overlay style as the other dialogs.
 */
export function LogoutOfflineWarning({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[130000] flex items-center justify-center bg-black/25 px-6 backdrop-blur-[1.25px]"
      style={{ pointerEvents: "auto" }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">
          You&rsquo;re offline
        </h2>
        <p className="mb-5 text-sm text-gray-600">
          Some plays haven&rsquo;t been saved yet. While offline they can&rsquo;t
          be uploaded, so they&rsquo;ll be lost if you sign out now. Connect to
          the internet first to save them.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Log out anyway
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LogoutOfflineWarning;
